// Shared types between frontend and the /api contracts.
// These are public contracts on the wire - rename only via deliberate migration.

export type Role = 'admin' | 'user' | null

export interface Me {
  isAuthenticated: boolean
  upn: string | null
  name: string | null
  userId: string | null      // Entra object ID
  dbUserId: number | null    // dbo.Users.id - resolved server-side via UPSERT on first sign-in / subsequent calls
  role: Role
  isAdmin: boolean
  groups: string[]            // Entra group IDs the user is a member of
  hasFinancial: boolean       // Per-user gate; resolved server-side
}

export interface EventType {
  id: number
  name: string
  colour_bg: string
  colour_text: string
  colour_border: string
  sort_order: number
  is_active: boolean
}

export interface Department {
  id: number
  name: string
  colour: string
  sort_order: number
  is_active: boolean
}

// 'Idea' is the pre-Draft, conceptual status: a candidate event being modelled
// for viability in Forecast Studio. It is NOT on the operating plan and is
// excluded from every operational read until promoted to 'Draft' on commit.
export type EventStatus = 'Idea' | 'Draft' | 'In Development' | 'Live' | 'Complete' | 'Cancelled'

// Wire-shape for an Event in list/detail views. camelCase because the API
// reshapes from snake_case DB columns to camelCase JSON. Dates are ISO
// YYYY-MM-DD strings (no timezone) - calendar dates, not timestamps.
// "Seats" choice on the Ticketing tab's Carriages card (info only, like the
// formation): whether the MERAC sessions should be built with no seat selection
// or with the seat sets as defined by the formation. null = not chosen yet.
export type SeatSelection = 'none' | 'sets'

// A ticket assigned to a carriage (dbo.EventCarriageTickets row): a quantity of
// seats for EITHER a ticket-build type (source 'em', dbo.EventTicketTypes) OR a
// MERAC ticket class already selling on the event (source 'merac', from the
// synced sales — db/089, no Event Manager ticket type needed). The name and
// price come with the row; a MERAC class's price is what it has sold at so far
// (gross ÷ sold) and, with `sold`, is financial data — null without it.
export type CarriageTicketSource = 'em' | 'merac'
export interface CarriageTicketAssignment {
  source: CarriageTicketSource
  eventTicketTypeId: number | null   // source 'em'
  ticketClassMeracId: string | null  // source 'merac'
  name: string
  category: string | null            // EM catalogue category; 'MERAC' for a class
  pricePence: number | null
  sold: number | null                // MERAC only: tickets sold so far
  quantity: number                   // seats allocated on this carriage
}
// What the client sends for one assignment: exactly one id + the quantity.
export interface CarriageTicketInput {
  eventTicketTypeId?: number | null
  ticketClassMeracId?: string | null
  quantity: number
}
// What the Assign dialog may offer on an event (GET /api/events/{id}/carriages/ticket-options).
export interface CarriageTicketOptions {
  merac: { ticketClassMeracId: string; name: string; pricePence: number | null; sold: number | null }[]
  em: { eventTicketTypeId: number; name: string; category: string; pricePence: number | null }[]
}
// A carriage INSTANCE in an event's formation (dbo.EventCarriages row) with its
// ticket assignments — the Carriages card's working shape (GET/PUT
// /api/events/{id}/carriages). `seats` comes from the catalogue; null = unknown code.
export interface CarriageInstance {
  id: number
  code: string
  sortOrder: number
  seats: number | null
  tickets: CarriageTicketAssignment[]
}
export interface EventCarriagesPayload {
  carriages: CarriageInstance[]
  seatSelection: SeatSelection | null
}
export interface CarriageInput { id?: number; code: string }

export interface EventListItem {
  id: number
  name: string
  status: EventStatus
  startDate: string                 // min of dates (envelope)
  endDate: string                   // max of dates (envelope)
  dates: string[]                   // the event's day set (item 7), sorted ISO
  carriages: string[]               // stock codes in FORMATION order, north → south (info only)
  seatSelection: SeatSelection | null // "Seats" pills on the Carriages card (info only)
  progress: number
  location: string | null
  capacity: number | null
  description: string | null
  type: {
    id: number
    name: string
    colourBg: string
    colourText: string
    colourBorder: string
  }
  manager: {
    id: number
    name: string | null
    avatarInitials: string | null
  } | null
  // MERAC binding (one code → one event) — populated from
  // dbo.MeracEventMap. null when the event has no MERAC link.
  meracEventCode: string | null
  // MERAC gross booked revenue (pence) for the event, from the events-list
  // read. null for non-financial users or events with no MERAC sales. Rides
  // the same response as meracEventCode so the card figure can't desync from
  // the badge.
  meracGrossPence?: number | null
  // Tweak T2a — archive state. Both null = active event; both set =
  // archived. T2b will hide archived events from default views and
  // render the read-only mode on click-through.
  archivedAt: string | null         // ISO timestamp from UTC
  archivedById: number | null
}

// Sidebar listing row (GET /api/events/archived). Slimmer than
// EventListItem — only the columns the Archive page renders.
export interface ArchivedEventListItem {
  id: number
  name: string
  status: EventStatus
  startDate: string
  endDate: string
  archivedAt: string
  archivedBy: {
    id: number
    name: string | null
    avatarInitials: string | null
  } | null
  type: {
    name: string
    colourBg: string
    colourText: string
    colourBorder: string
  }
}

// POST /api/events/{id}/archive response. closedActions /
// closedPlanTasks let the frontend show a "closed N tasks" toast.
export interface ArchiveEventResult {
  archived: true
  closedActions: number
  closedPlanTasks: number
}

// GET /api/events/{id}/archive/preview — count of what WOULD be
// closed if Archive is clicked. Drives the confirm modal's body text.
export interface ArchivePreview {
  activeActions: number
  activePlanTasks: number
}

// GET /api/events/{id}/build-checklist (Tweak T4). Each step derives
// from real table state - see api/src/handlers/buildChecklist.ts for
// the per-step rules. `reason: 'tbd'` flags steps 7-9 which Chris
// will spec later; the frontend renders them dimmer than derived
// steps. percentComplete is rounded done/total over derived steps
// only (TBD steps don't drag the bar down before they're real).
export type BuildChecklistReason = 'derived' | 'tbd'

export interface BuildChecklistStep {
  slug: string
  label: string
  done: boolean
  reason: BuildChecklistReason
}

export interface BuildChecklistPayload {
  steps: BuildChecklistStep[]
  percentComplete: number
}

// PUT /api/events/{id} request body. Full-replacement (PUT semantics): every
// editable field must be supplied. event_type is intentionally NOT in this
// shape - type changes are gated separately because of their downstream
// colour/template implications.
export interface EventUpdate {
  name: string
  status: EventStatus
  eventTypeId: number       // event type is now editable from the Edit panel
  startDate: string         // ISO YYYY-MM-DD (envelope min)
  endDate: string           // ISO YYYY-MM-DD (envelope max)
  dates?: string[]          // explicit day set (item 7); omit to keep the range
  carriages?: string[]      // stock codes in formation order, north → south (info only); omit to leave unchanged
  location: string | null
  capacity: number | null
  description: string | null
  progress: number          // 0..100
  managerUserId: number | null
  // Tri-state MERAC binding:
  //   - omit field   → server keeps existing binding (no change)
  //   - explicit null → server unbinds (deletes MeracEventMap row)
  //   - string        → server sets/replaces binding to this code
  meracEventCode?: string | null
}

// POST /api/events request body. Status is server-defaulted to 'Draft' and
// progress to 0; the client doesn't get to choose. event_type is required
// here because every event has a type from the moment it's created.
export interface EventCreate {
  name: string
  eventTypeId: number
  startDate: string
  endDate: string
  dates?: string[]                 // explicit day set (item 7); else the range
  carriages?: string[]             // stock codes in formation order, north → south (info only)
  location: string | null
  capacity: number | null
  description: string | null
  managerUserId: number | null
  meracEventCode?: string | null  // optional MERAC binding on create
  asIdea?: boolean                // create in the pre-Draft 'Idea' status (Forecast Studio)
}

// dbo.MeracEventCodes catalog row — used by the create/edit event
// dropdown to bind an event to a MERAC source.
export interface MeracEventCode {
  code: string
  lookupGroup: string  // '19' = event-rollup codes (current default)
  description: string
  isActive: boolean
  lastSyncedAt: string | null
}

// /api/users response shape - minimal, just enough to populate the manager
// picker. If we need more (department, role_label, phone) we'll extend then.
export interface UserOption {
  id: number
  name: string | null
  avatarInitials: string | null
}

// /api/people response shape (v2). Richer than UserOption — used by
// PersonPicker / PersonMultiPicker (spec 15 §3, spec 17 §3) which need to
// show role + department badges and filter by isAdmin / hasFinancial.
// Stubbed against 25 v5 PEOPLE entries; swap to SQL when dbo.Users is
// fully populated.
export interface Person {
  id: string
  name: string
  role: string
  dept: string
  phone: string
  avatarInitials: string
  isAdmin: boolean
  hasFinancial: boolean
}

// ---- Activities (Phase 2 step 1) -------------------------------------------

// An attachment on an activity. v1: URL-only (link to Drive doc, supplier
// brief, image hosted elsewhere). v2 may add Blob-backed file uploads -
// when it does, an optional kind/contentType/sizeBytes can be added without
// breaking the URL-only shape.
export interface Attachment {
  label: string
  url: string
}

// Activity category — nullable on the per-event side because the
// pre-mig-035 rows didn't have one. Library-pulled rows always
// carry a value; admin-added custom rows may pick one. Mirrors
// LIBRARY_ACTIVITY_CATEGORIES below — same allow-list, same order.
export const ACTIVITY_CATEGORIES = [
  'Entertainment', 'Photo', 'Games', 'Crafts',
  'Story', 'Trail', 'Ride', 'Play',
  'Workshop', 'Catering', 'Other'
] as const
export type ActivityCategory = typeof ACTIVITY_CATEGORIES[number]

// Wire shape for an activity. timeText is intentionally a free string
// ("11:00 / 13:00 / 15:00", "All day", "Hourly from 10:00") because the
// prototype's data has no consistent structure to it - matches how
// Charlotte and the suppliers actually describe activity timings.
export interface Activity {
  id: number
  eventId: number
  // mig 035 — non-null = copied from LibraryActivities at this slug.
  // Frontend uses it to render a "from library" badge so the public
  // contract stays visible.
  librarySlug: string | null
  icon: string | null                       // mig 035 — lucide icon name (mirrors library row)
  isEnabled: boolean                        // mig 035 — toggle without delete
  category: ActivityCategory | null         // mig 035 — nullable for legacy rows
  name: string
  description: string | null
  location: string | null
  timeText: string | null
  staffCount: number | null
  supplier: string | null
  attachments: Attachment[]
  sortOrder: number
}

// POST and PUT bodies are identical for activities (full-replacement
// PUT semantics, same field set on create). Aliased to keep call-sites
// readable; if they ever diverge we'll split them then.
//
// library_slug is NOT in the create/update body — it's set server-side
// on copy-from-library and otherwise stays null for custom rows.
export interface ActivityCreate {
  name: string
  description: string | null
  location: string | null
  timeText: string | null
  staffCount: number | null
  supplier: string | null
  attachments: Attachment[]
  sortOrder: number
  icon?: string | null                      // mig 035 — optional
  isEnabled?: boolean                       // mig 035 — defaults true server-side
  category?: ActivityCategory | null        // mig 035 — optional, may be null
}

export type ActivityUpdate = ActivityCreate

// ---- Library Activities (mig 035, Library Build PR B) ------------------
//
// Global catalog of reusable programme items. Per-event rows live in
// dbo.Activities (extended by mig 035 with library_slug / icon /
// is_enabled / category). Library defaults snapshot into the live
// per-event columns at copy time — same copy-at-apply pattern as
// Library Components.

export const LIBRARY_ACTIVITY_CATEGORIES = ACTIVITY_CATEGORIES
export type LibraryActivityCategory = ActivityCategory

export interface LibraryActivity {
  id: number
  slug: string                              // public contract — kebab-case, never rename
  name: string
  icon: string | null                        // lucide icon name
  category: LibraryActivityCategory
  description: string | null
  defaultLocation: string | null
  defaultTime: string | null                 // free string per prototype shape
  defaultStaff: number | null
  defaultSupplier: string | null
  autoAdd: boolean                           // true → auto-copy onto new events
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface LibraryActivityCreate {
  slug: string
  name: string
  category: LibraryActivityCategory
  icon?: string | null
  description?: string | null
  defaultLocation?: string | null
  defaultTime?: string | null
  defaultStaff?: number | null
  defaultSupplier?: string | null
  autoAdd?: boolean
  sortOrder?: number
}

export interface LibraryActivityPatch {
  // slug NOT patchable — it's the public contract for the per-event
  // copies. Soft-delete + re-create if a new slug is needed.
  name?: string
  category?: LibraryActivityCategory
  icon?: string | null
  description?: string | null
  defaultLocation?: string | null
  defaultTime?: string | null
  defaultStaff?: number | null
  defaultSupplier?: string | null
  autoAdd?: boolean
  sortOrder?: number
}

// Response from POST /events/{id}/activities/copy-from-library.
// Counts plus the inserted rows. Slugs that didn't match an active
// library row OR are already on the event are reported as `skipped`.
export interface CopyActivitiesFromLibraryResult {
  inserted: number
  skipped: number
  rows: Activity[]
}

// ---- Components (Phase 2 step 2) -------------------------------------------

// Canonical category list. Source of truth for display order and the
// allowed-values check (which the API also enforces via SQL CHECK and
// an in-handler allow-list). Adding a category is a three-place change:
// here, the SQL CK_Components_Category constraint, and the handler's
// CATEGORIES const.
//
// Two sets coexist because mig 034 widened the CK to support both:
//   - Rolling-stock / cost-flavoured (original mig 006 set — for
//     production component planning: locos, coaches, supplier services)
//   - Ops / event-flavoured (mig 034 — for library-pulled rows:
//     parking, first aid, comms, etc.)
// Programme appears in both sets so the union has 14 values total.
export const COMPONENT_CATEGORIES = [
  // Rolling-stock / cost (mig 006)
  'Locomotive', 'Coaches', 'Supplier', 'Ops Resource', 'Hospitality',
  // Ops / event (mig 034 — library-pulled rows)
  'Logistics', 'Safety', 'Comms', 'Accessibility', 'Onboarding',
  'F&B', 'Retail',
  // Shared / catch-all
  'Programme', 'Other'
] as const
export type ComponentCategory = typeof COMPONENT_CATEGORIES[number]

// Status enum. Same source-of-truth pattern.
export const COMPONENT_STATUSES = ['Proposed', 'Confirmed', 'Cancelled'] as const
export type ComponentStatus = typeof COMPONENT_STATUSES[number]

// Wire shape for a component.
//
// costEstimate visibility: the SERVER nulls this field for users without
// hasFinancial regardless of the row's actual value. Clients distinguish
// "no cost set" from "cost hidden from me" using me.hasFinancial - a
// non-financial user always sees null here, a financial user sees null
// only when no cost has been entered.
export interface Component {
  id: number
  eventId: number
  // mig 034 — non-null = copied from LibraryComponents at this slug.
  // Frontend uses it to render a "from library" badge and disable
  // rename UX so the public contract stays stable.
  librarySlug: string | null
  icon: string | null                    // mig 034 — lucide icon name (mirrors library row)
  isEnabled: boolean                     // mig 034 — toggle without delete
  category: ComponentCategory
  name: string
  description: string | null
  quantity: number | null
  costEstimate: number | null            // null for non-financial users (server-gated)
  notes: string | null
  status: ComponentStatus
  attachments: Attachment[]
  sortOrder: number
}

// POST and PUT bodies are identical for components (full-replacement PUT
// semantics, same field set on create). Aliased to keep call-sites readable.
//
// Non-financial callers may send `costEstimate: null` - the server ignores
// the field on POST (forces null) and omits it from the SET clause on PUT
// (preserving the existing value). Financial callers send the real value.
//
// library_slug is NOT in the create/update body — it's set server-side
// on copy-from-library and otherwise stays null for custom rows.
export interface ComponentCreate {
  category: ComponentCategory
  name: string
  description: string | null
  quantity: number | null
  costEstimate: number | null
  notes: string | null
  status: ComponentStatus
  attachments: Attachment[]
  sortOrder: number
  icon?: string | null                   // mig 034 — optional
  isEnabled?: boolean                    // mig 034 — defaults true server-side
}

export type ComponentUpdate = ComponentCreate

// ---- Library Components (mig 034, Library Build PR A) -------------------
//
// Global catalog of reusable event components. Per-event rows
// (EventComponent below) are snapshotted from this catalog at the
// moment they're pulled onto an event — copy-at-apply, same pattern
// as Checklists. Library edits do NOT propagate to existing events.

export const LIBRARY_COMPONENT_CATEGORIES = [
  'Logistics', 'Safety', 'Comms', 'Accessibility',
  'Onboarding', 'Programme', 'F&B', 'Retail'
] as const
export type LibraryComponentCategory = typeof LIBRARY_COMPONENT_CATEGORIES[number]

export interface LibraryComponent {
  id: number
  slug: string                             // public contract — kebab-case, never rename
  name: string
  icon: string | null                       // lucide icon name
  category: LibraryComponentCategory
  description: string | null
  autoAdd: boolean                          // true → auto-copy onto new events
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface LibraryComponentCreate {
  slug: string
  name: string
  category: LibraryComponentCategory
  icon?: string | null
  description?: string | null
  autoAdd?: boolean
  sortOrder?: number
}

export interface LibraryComponentPatch {
  // slug NOT patchable — it's the public contract for the per-event
  // copies. Soft-delete + re-create if a new slug is needed.
  name?: string
  category?: LibraryComponentCategory
  icon?: string | null
  description?: string | null
  autoAdd?: boolean
  sortOrder?: number
}

// Per-event components live in the existing dbo.Components table
// (mig 006), extended by mig 034 with `library_slug` / `icon` /
// `is_enabled` so library-pulled rows and custom rows share one
// surface. See the Component interface above — that's the shape
// the per-event API returns on /events/{id}/components.

// Response from POST /events/{id}/components/copy-from-library.
// Counts plus the inserted rows. Slugs that didn't match an active
// library row OR are already on the event are reported as `skipped`.
export interface CopyFromLibraryResult {
  inserted: number
  skipped: number
  rows: Component[]
}

// ---- Services (Phase 2 step 3) ---------------------------------------------

// Wire shape for a train service / timetable row.
//
// timeText is HH:MM 24-hour, zero-padded. Server validates the shape with a
// regex matching the SQL CK_Services_TimeText constraint. Sorting in lists
// is by timeText (lexicographic on HH:MM works because zero-padded), then
// sortOrder, then id.
//
// One timetable per event in v1 - bookings + per-day variation deferred.
// No financial gating, no attachments on services.
export interface ServiceCrewMember {
  userId: number
  name: string
  avatarInitials: string
  roleName: string                  // for the chip tooltip
}

export interface Service {
  id: number
  eventId: number
  serviceLabel: string         // free text, e.g. "Service 1", "1T01"
  timeText: string             // "HH:MM" 24-hour
  loco: string | null
  originStation: string | null
  destinationStation: string | null
  capacity: number | null
  notes: string | null
  sortOrder: number
  // v2 spec 15 fields (migration 019). v1 callers can ignore.
  dateOverride?: string | null // ISO yyyy-mm-dd — when set, runs only on this date
  booked?: number              // seat count; MERAC sync target
  // Feature 2 fields (migration 062).
  serviceType?: ServiceType    // 'event' (default) | 'pullman' | 'dining' | 'shuttle' | 'other'
  pullmanMode?: PullmanMode | null // for pullman/dining: 'block' | 'parallel'
  arrivalTime?: string | null  // HH:MM return/end time
  setFormation?: string | null // e.g. '64 x 3, 39 x 2'
  // Derived field populated by the list handler only. Per-service POST/PUT
  // responses omit it - the frontend gets crew updates by refetching the list
  // (useUpdateService / useCreateService invalidate the services query).
  crew?: ServiceCrewMember[]
}

// Feature 2 (mig 062) — service classification + Pullman dining state.
export const SERVICE_TYPES = ['event', 'pullman', 'dining', 'shuttle', 'other'] as const
export type ServiceType = typeof SERVICE_TYPES[number]
export const PULLMAN_MODES = ['block', 'parallel'] as const
export type PullmanMode = typeof PULLMAN_MODES[number]

// Per-event service header (the doc's JOURNEY / RUN TIME / SET / CAPACITY).
export interface EventServiceInfo {
  journey: string | null
  runTime: string | null
  setFormation: string | null
  capacityNote: string | null
  notes: string | null
}

// Batched all-events service-timetable report (GET /api/reports/service-timetable).
// One payload for the whole "Timings & Set Plan" PDF — avoids the per-event
// fan-out that stalled the client-side report.
export interface ServiceTimetableReportEvent {
  eventId: number
  name: string
  typeName: string
  startDate: string
  endDate: string
  source: 'manual' | 'merac'
  info: EventServiceInfo | null
  services: PresTimetableRow[]
}

export interface ServiceTimetableReportPayload {
  events: ServiceTimetableReportEvent[]
}

// Presentation timetable (GET /api/events/:id/presentation-timetable + the
// batched report). The timetable AS PRINTED: manual dbo.Services when curated,
// otherwise the full per-day MERAC session projection. A superset of the fields
// the print components render — MERAC rows carry a `date` and leave loco / set
// formation / Pullman mode null. See api/src/lib/presentationTimetable.ts.
export interface PresTimetableRow {
  id: string | number
  source: 'manual' | 'merac'
  date: string | null              // yyyy-mm-dd; null on undated manual rows
  timeText: string
  arrivalTime: string | null
  serviceLabel: string
  loco: string | null
  originStation: string | null
  destinationStation: string | null
  setFormation: string | null
  capacity: number | null
  pullmanMode: PullmanMode | null
  serviceType: ServiceType
  booked: number | null
}

export interface PresentationTimetablePayload {
  source: 'manual' | 'merac'
  meracEventId: string | null
  services: PresTimetableRow[]
}

// Timetable wire shape (GET /api/events/:eventId/timetable). Returned
// from a dual-source endpoint that picks dbo.Services or MeracSessions
// depending on whether the event has a MeracEventMap binding. MERAC-
// derived rows have a synthetic string id (e.g. 'merac-abc123'),
// null loco/origin/destination/capacity (MERAC gaps), and a populated
// meracBooked. Manual rows mirror Service[] with meracBooked=null.
export interface TimetableRow {
  id: number | string
  source: 'manual' | 'merac'
  serviceLabel: string
  timeText: string
  // Per-row operating date, YYYY-MM-DD. MERAC rows always carry one
  // (from session_start_at). Manual rows carry one only when
  // date_override is set — null means "applies to every operating
  // day" and the day-selector treats those rows as always visible.
  date: string | null
  loco: string | null
  originStation: string | null
  destinationStation: string | null
  capacity: number | null
  meracBooked: number | null
  notes: string | null
  sortOrder: number
  crew: ServiceCrewMember[]
}

export interface TimetablePayload {
  source: 'manual' | 'merac'
  meracEventId: string | null  // populated when source='merac'
  services: TimetableRow[]
}

// POST and PUT bodies are identical (full-replacement PUT semantics).
// Same alias pattern as Activity / Component.
export interface ServiceCreate {
  serviceLabel: string
  timeText: string
  loco: string | null
  originStation: string | null
  destinationStation: string | null
  capacity: number | null
  notes: string | null
  sortOrder: number
  dateOverride?: string | null
  booked?: number
  serviceType?: ServiceType
  pullmanMode?: PullmanMode | null
  arrivalTime?: string | null
  setFormation?: string | null
}

export type ServiceUpdate = ServiceCreate

// ---- v2 Schedule (spec 15) -------------------------------------------------

export interface ServiceCrew {
  id: number
  serviceId: number
  personId: string             // matches Person.id from /api/people
  role: 'guard' | 'steward'
  notes: string | null
}

export interface AssignCrewBody {
  guard: string | null
  stewards: string[]
  notes: string
}

export interface OperatingDay {
  id: number
  eventId: number
  date: string                 // ISO yyyy-mm-dd
  running: boolean
  serviceCount: number | null
  note: string | null
}

export interface UpsertOperatingDayBody {
  running: boolean
  serviceCount: number | null
  note: string
}

export interface ImportWttBody {
  services: Array<{
    time: string
    service: string
    loco: string
    from: string
    to: string
    capacity: number
    notes?: string
  }>
  preserveCrew: boolean
}

export interface ImportWttResult {
  imported: number
  crewPreserved: number
  crewDropped: number
}

export interface NameOption {
  id: number
  name: string
}

// ---- Roster (rebuild — migrations 039 + 040 + 042) -----------------------
//
// Global Roles catalogue + per-event EventRoles toggle + multi-person
// RosterAssignments (rows = filled cells; empty cells are absence-of-row).
// Replaces the legacy v1 RosterRoles + auto-fill RosterAssignments model.

// Role types (informational). Drives chip styling on the picker; not
// gating. Stays in lockstep with the CK_Roles_RoleType constraint in
// migration 039 and the CATEGORIES allow-list in api/src/handlers/role.ts.
export const ROLE_TYPES = ['staff', 'volunteer', 'contractor'] as const
export type RoleType = typeof ROLE_TYPES[number]

// A role in the global catalogue. Source of truth for grid row order
// (via sortOrder) and for the Schedule tab's crew-chip filter (via
// affectsServices — true for Event Manager / Duty Manager / Controller
// / Station Foreman; mirrors the legacy mig 009 selection).
export interface LibraryRole {
  id: number
  slug: string                                // public contract — kebab-case, never rename
  name: string
  description: string | null
  defaultShiftStart: string | null            // "HH:mm" 24-hour, null = no default
  defaultShiftEnd:   string | null
  roleType: RoleType
  affectsServices: boolean
  // Part of the default "roles to call for" set (mig 055). Seeds the volunteer
  // call composer for events with no existing call (item 1).
  isDefault: boolean
  isActive: boolean
  sortOrder: number
  // Per-event target headcount per day. Only populated on the roster GET
  // (sourced from EventRoles.need_per_day); null/absent on the global list.
  needPerDay?: number | null
  createdAt: string
  updatedAt: string
}

// POST /api/access/roles body. slug + name required; everything else
// optional with sensible server-side defaults.
export interface LibraryRoleCreate {
  slug: string
  name: string
  description?: string | null
  defaultShiftStart?: string | null           // "HH:mm" or null
  defaultShiftEnd?:   string | null
  roleType?: RoleType
  affectsServices?: boolean
  isDefault?: boolean
  sortOrder?: number
}

// PATCH /api/access/roles/{id} body. slug NOT patchable — it's the
// public contract. All other fields optional; absent fields preserve
// current value.
export interface LibraryRolePatch {
  name?: string
  description?: string | null
  defaultShiftStart?: string | null
  defaultShiftEnd?:   string | null
  roleType?: RoleType
  affectsServices?: boolean
  isDefault?: boolean
  sortOrder?: number
}

// A roster cell — one row per (event, role, date, user). Multi-person
// cells are first-class (no UNIQUE constraint on event_id+role_id+date);
// empty cells are absence-of-row. timeStart / timeEnd are nullable
// per-chip overrides — null means "use Role.defaultShift*".
export interface RosterAssignment {
  id: number
  eventId: number
  roleId: number
  assignmentDate: string                      // ISO YYYY-MM-DD
  userId: number
  userName: string | null
  userAvatarInitials: string | null
  timeStart: string | null                    // "HH:mm" or null
  timeEnd:   string | null
  sortOrder: number
  note: string | null
  status: RosterAssignmentStatus              // assigned | confirmed (mig 053)
  confirmedAt: string | null
}

export type RosterAssignmentStatus = 'assigned' | 'confirmed'

// GET /api/events/{id}/roster response. roles is the active set for
// THIS event (filtered by EventRoles), in global Roles.sortOrder.
// days is the server-computed list of date strings from event.startDate
// to event.endDate inclusive. assignments is the array of filled cells.
export interface RosterResponse {
  roles: LibraryRole[]
  days: string[]
  assignments: RosterAssignment[]
  // User ids currently signed on (open EventLiveShift) — drives the live
  // green pulse on chips. Empty outside the event window.
  signedOnUserIds: number[]
}

// POST /api/events/{id}/assignments body. sort_order is auto-assigned
// server-side (MAX existing + 10 for the same cell) — admins reorder
// post-hoc via PATCH.
export interface RosterAssignmentCreate {
  roleId: number
  assignmentDate: string                      // ISO YYYY-MM-DD
  userId: number
  timeStart?: string | null                   // "HH:mm" or null
  timeEnd?:   string | null
  note?: string | null
}

// PATCH /api/access/roster-assignments/{id} body. Partial — absent
// fields preserve current value. role/user/event/date are NOT patchable
// (delete and re-create if those need to change).
export interface RosterAssignmentPatch {
  timeStart?: string | null
  timeEnd?:   string | null
  sortOrder?: number
  note?: string | null
  // Drag-to-move (role and/or date) and click-to-confirm (status). A move
  // resets a confirmed chip back to 'assigned' unless status is set explicitly.
  roleId?: number
  assignmentDate?: string
  status?: RosterAssignmentStatus
}

// GET /api/events/{id}/roles response — the per-event role toggle.
// Returns ALL global roles plus an `inUse` flag for each, so the
// Configure Roles multi-picker can render the full list with ticked
// state. Active-only filter applied server-side.
export interface EventRoleSummary {
  role: LibraryRole
  inUse: boolean
  notes: string | null
  needPerDay: number | null
}

// POST /api/events/{id}/roles/set body. Replace-the-set semantics:
// any role_id in the body that's not in EventRoles is added; any in
// EventRoles but not in the body is removed.
export interface EventRolesSetBody {
  roleIds: number[]
  // Optional per-role target headcount per day, keyed by role id.
  needByRole?: Record<string, number | null>
}

// ---- Events Live volunteer loop (mig 053) --------------------------------

export type VolunteerOfferStatus = 'pending' | 'approved' | 'declined'

// One availability offer from InfoHub, as the manager Availability inbox sees it.
export interface VolunteerOffer {
  id: number
  eventId: number
  userId: number
  mode: 'whole' | 'days'
  note: string | null
  readConfirmed: boolean
  anyRole: boolean              // offered for any role (item 3)
  status: VolunteerOfferStatus
  submittedAt: string
  reviewedAt: string | null
  volunteer: {
    id: number
    name: string | null
    avatarInitials: string | null
    email: string | null
    phone: string | null
    emergencyContactName: string | null
    emergencyContactPhone: string | null
    sinceYear: number | null
    priorEvents: number
  }
  roleIds: number[]
  days: string[]
  assignments: Array<{ roleId: number; date: string; status: RosterAssignmentStatus }>
  placedCount: number
  confirmedCount: number
}

// The per-event call for volunteers (composer + InfoHub banner driver).
export interface VolunteerCall {
  id?: number
  eventId: number
  message: string | null
  audience: string | null
  isActive: boolean
  publishedAt?: string | null
  closedAt?: string | null
  roleIds: number[]
}

export interface VolunteerCallSetBody {
  message: string | null
  audience: string | null
  roleIds: number[]
  active: boolean
}

// One per-event availability row (mig 041, PR R3). Presence of a row =
// "this user is available for this role on this date in this event".
// Absence = no statement / not available. Binary.
export interface RosterAvailability {
  id: number
  userId: number
  eventId: number
  roleId: number
  availableDate: string                       // ISO YYYY-MM-DD
  note: string | null
  userName: string | null
  userAvatarInitials: string | null
}

// One entry in the upsert batch. `available: true` MERGEs the row in
// (and updates the note if it changed); `available: false` deletes the
// matching row. `userId` defaults to caller on the server — admins
// may set explicitly to mark on behalf of someone else.
export interface RosterAvailabilityEntry {
  userId?: number
  roleId: number
  date: string                                // ISO YYYY-MM-DD
  available: boolean
  note?: string | null
}

export interface RosterAvailabilityUpsertBody {
  entries: RosterAvailabilityEntry[]
}

export interface RosterAvailabilityUpsertResult {
  inserted: number
  deleted: number
  unchanged: number
  rows: RosterAvailability[]
}

// ---- Dashboard (Phase 2 step 6) --------------------------------------------

export interface DashboardKpis {
  liveEvents: number
  upcoming14d: number
  eventsThisYear: number
  avgBuildProgress: number          // integer 0..100
}

export interface DashboardLiveBanner {
  eventId: number
  eventName: string
  typeChip: { name: string; colourBg: string; colourText: string }
  dayOfRun: number
  totalDays: number
  nextServiceTimeText: string | null
}

export interface DashboardUpcoming {
  id: number
  name: string
  status: EventStatus
  startDate: string
  endDate: string
  progress: number
  // Tweak T2b — archived events are filtered out of the upcoming query
  // server-side, so this is null in practice. Surfaced for defence in
  // depth: if a future surface re-pipes archived events through the
  // upcoming list, UpcomingEvents.tsx fades them and stamps the badge.
  archivedAt: string | null
  type: { name: string; colourBg: string; colourText: string; colourBorder: string }
  manager: { id: number; name: string | null; avatarInitials: string | null } | null
}

export interface DashboardFeedItem {
  timestamp: string
  actor: { name: string; avatarInitials: string }
  summary: string
  entityType: 'event' | 'activity' | 'component' | 'service' | 'roster'
  entityId: number
  eventId: number | null
}

export interface DashboardPayload {
  kpis: DashboardKpis
  liveBanner: DashboardLiveBanner | null
  upcoming: DashboardUpcoming[]
  feed: DashboardFeedItem[]
}

export type WeatherCode = 'sun' | 'partly' | 'cloud' | 'rain' | 'storm' | 'snow' | 'fog'

export interface WeatherDay {
  date: string
  minC: number
  maxC: number
  code: WeatherCode
  summary: string
}

export interface WeatherPayload {
  location: 'Pickering'
  fetchedAt: string
  stale?: boolean
  days: WeatherDay[]
}

// ---- Checklists (spec 03) --------------------------------------------------

export interface ChecklistTemplateItem {
  id: number
  templateId: number
  section: string
  title: string
  description: string | null
  defaultOwnerRole: string | null
  daysBeforeEvent: number | null
  sortOrder: number
}

export interface ChecklistTemplateSummary {
  id: number
  name: string
  description: string | null
  isActive: boolean
  itemCount: number
  createdAt: string | null
  updatedAt: string | null
}

export interface ChecklistTemplate extends ChecklistTemplateSummary {
  items: ChecklistTemplateItem[]
}

// Wire body for POST /api/checklist-templates and PUT .../{id}.
export interface ChecklistTemplateBody {
  name: string
  description: string | null
  items: Array<{
    section: string
    title: string
    description: string | null
    defaultOwnerRole: string | null
    daysBeforeEvent: number | null
    sortOrder: number
  }>
}

export interface EventChecklistItem {
  id: number
  checklistId: number
  sourceTemplateItemId: number | null
  section: string
  title: string
  description: string | null
  owner: { id: number; name: string | null; avatarInitials: string | null } | null
  dueDate: string | null
  isDone: boolean
  doneBy: { id: number; name: string | null } | null
  doneAt: string | null
  sortOrder: number
  notes: string | null
}

export interface AppliedTemplate {
  id: number          // EventChecklists row id
  templateId: number | null
  templateName: string | null
  appliedAt: string
  appliedBy: number
}

export interface EventChecklistResponse {
  checklist: {
    id: number
    eventId: number
    appliedTemplates: AppliedTemplate[]
  } | null
  items: EventChecklistItem[]
}

// Body for POST /api/events/{id}/checklist/items
export interface EventChecklistItemAdd {
  section: string
  title: string
  description?: string | null
  ownerUserId?: number | null
  dueDate?: string | null
  notes?: string | null
  sortOrder?: number
}

// Body for PATCH /api/events/{id}/checklist/items/{itemId}
export interface EventChecklistItemPatch {
  title?: string
  description?: string | null
  ownerUserId?: number | null
  dueDate?: string | null
  notes?: string | null
  sortOrder?: number
  isDone?: boolean
}

// ---- Risks (spec 04) -------------------------------------------------------

export interface HazardLibraryEntry {
  id: number
  hazard: string
  category: string
  defaultLikelihood: number
  defaultSeverity: number
  suggestedControls: string | null
  sortOrder: number
}

// POST body. Defaults applied server-side: defaultLikelihood +
// defaultSeverity to 3 (mid-scale), suggestedControls to null,
// sortOrder to 0.
export interface HazardLibraryCreate {
  hazard: string
  category: string
  defaultLikelihood?: number
  defaultSeverity?: number
  suggestedControls?: string | null
  sortOrder?: number
}

// PATCH body — partial. Absent fields preserve current value.
export interface HazardLibraryPatch {
  hazard?: string
  category?: string
  defaultLikelihood?: number
  defaultSeverity?: number
  suggestedControls?: string | null
  sortOrder?: number
}

export interface EventRisk {
  id: number
  eventId: number
  sourceLibraryId: number | null
  hazard: string
  category: string
  inherentLikelihood: number
  inherentSeverity: number
  inherentScore: number
  controls: string
  residualLikelihood: number
  residualSeverity: number
  residualScore: number
  owner: { id: number; name: string | null; avatarInitials: string | null } | null
  signedOff: { by: { id: number; name: string | null }; at: string | null } | null
  notes: string | null
  sortOrder: number
  // v2 (migration 024). Nullable for v1 rows that haven't been touched.
  dept?: string | null
  description?: string | null
}

// Body for POST/PUT /api/events/{id}/risks (and /risks/{riskId}).
export interface EventRiskBody {
  hazard: string
  category: string
  inherentLikelihood: number
  inherentSeverity: number
  controls: string
  residualLikelihood: number
  residualSeverity: number
  ownerUserId: number | null
  notes: string | null
  sortOrder: number
  sourceLibraryId: number | null
  // v2 (migration 024). Optional in the wire body — v1 callers omit.
  dept?: string | null
  description?: string | null
}

// ---- Notes / decisions log (spec 09) ---------------------------------------

export interface EventNote {
  id: number
  eventId: number
  body: string
  isPinned: boolean
  pinnedLabel: string | null
  author: { id: number; name: string | null; avatarInitials: string | null }
  createdAt: string
  editedAt: string | null
}

export interface NoteCreateBody {
  body: string
  isPinned?: boolean
  pinnedLabel?: string | null
}

export interface NotePatchBody {
  body?: string
  isPinned?: boolean
  pinnedLabel?: string | null
}

// ---- Budget (spec 06) ------------------------------------------------------

export interface BudgetCategory {
  id: number
  name: string
  kind: 'cost' | 'revenue'
  sortOrder: number
}

export interface BudgetLine {
  id: number
  eventId: number
  category: BudgetCategory
  description: string
  plannedAmount: number
  committedAmount: number
  actualAmount: number
  baselinePlannedAmount: number | null
  baselinedAt: string | null
  baselinedByName: string | null
  linkedComponent: { id: number; name: string | null } | null
  notes: string | null
  sortOrder: number
}

export interface BudgetTotals {
  cost: { planned: number; committed: number; actual: number; baseline: number }
  revenue: { planned: number; committed: number; actual: number; baseline: number }
  net: number
  netVariancePct: number | null
}

export interface BudgetPayload {
  lines: BudgetLine[]
  totals: BudgetTotals
  baselined: boolean
  baselinedAt: string | null
  baselinedByName: string | null
}

export interface BudgetLineBody {
  categoryId: number
  description: string
  plannedAmount: number
  committedAmount: number
  actualAmount: number
  linkedComponentId: number | null
  notes: string | null
  sortOrder: number
}

// MERAC actuals rollup — BIGINT pence, net can be negative (refunds +
// adjustments). lastSessionAt is the freshness stamp; null when no
// sessions have landed for this event (or no MeracEventMap row).
export interface MeracRollupPayload {
  grossRevenuePence: number
  netRevenuePence: number
  sessionCount: number
  lastSessionAt: string | null
}

// GET /api/events/merac-summary — batch presence + ticket/income summary
// keyed by event_id, powering the Events list card badge. Money (pence) is
// null for non-financial users; hasData / ticketCount / lastRefreshed are
// always present. source = 'merac' | 'quaytickets' (mig 043).
export interface MeracSourceBreakdown {
  source: string
  ticketCount: number
  grossPence: number | null
}

export interface EventMeracSummary {
  hasData: boolean
  lastRefreshed: string | null      // ISO; MAX(ingested_at) for the event
  ticketCount: number
  sessionCount: number
  grossPence: number | null         // null for non-financial users
  netPence: number | null
  bySource: MeracSourceBreakdown[]
}

export type MeracSummaryMap = Record<number, EventMeracSummary>

// Event Financials (Sales seq Part 2) — read-only analytics surface for
// users with hasFinancial. All money values are pence integers (BIGINT on
// the wire). revenueNetPence is null when any underlying booking row has
// a NULL net_revenue_pence — the daily refunds run hasn't filled the
// column yet — in which case netPending=true and the netMargin KPI is
// computed off gross.
export interface FinancialsRevenueClass {
  ticketClassMeracId: string
  ticketClassName: string
  quantitySold: number
  grossPence: number
  netPence: number | null
  pctOfRevenue: number
}

export interface FinancialsCostCategory {
  category: string
  totalPence: number
  pctOfCosts: number
  lineCount: number
}

export interface FinancialsSalesByDay {
  date: string                  // 'YYYY-MM-DD' (operating day)
  ticketsSold: number
  grossRevenuePence: number
}

export interface FinancialsPayload {
  eventId: number
  asOf: string | null            // ISO; null when MERAC hasn't synced this event yet
  netPending: boolean
  kpis: {
    revenueGrossPence: number
    revenueNetPence: number | null
    totalCostsPence: number
    netMarginPence: number
    netMarginPct: number
    ticketsSold: number
    avgSpendPerVisitorPence: number
  }
  revenueByClass: FinancialsRevenueClass[]
  costsByCategory: FinancialsCostCategory[]
  salesByDay: FinancialsSalesByDay[]
}

// GET /api/events/{id}/income — A+B+C income breakdown for the Financials-tab
// income panel. All money is pence; the endpoint is financial-gated. booked =
// advance (MeracSessions); gate = event-PLU walk-up at the till; ticketTotal =
// booked + gate (lens B); onTheDay = all till takings on event dates by
// pluCategory (lens A); the booked-vs-gate split is lens C.
export interface EventIncomeCategory {
  category: number
  label: string
  qty: number
  pence: number
}

export interface EventIncomePayload {
  eventId: number
  asOf: string | null
  hasData: boolean
  online: { qty: number; pence: number }   // advance web bookings (bkhIsInternet=1)
  inhouse: { qty: number; pence: number }   // counter / walk-up (everything else)
  refunds: { pence: number }                // real refunds (Path A), netted but shown
  ticketTotalQty: number
  ticketTotalPence: number                  // NET (online + inhouse − refunds) on the consolidated basis
  source: 'consolidated' | 'legacy'         // consolidated = Sync Hub canonical net basis
  onTheDay: { totalQty: number; totalPence: number; byCategory: EventIncomeCategory[] }
}

// ---- Event actions (spec 30) ---------------------------------------------
//
// Per-event action register — owner-assigned to-do items distinct from
// the project-plan Gantt (spec 32) and the build checklist (derived).
// Data model: dbo.EventActions (mig 026), seeded in PR #11 phase 2.

export type ActionPriority = 'low' | 'medium' | 'high'
export type ActionStatus   = 'open' | 'in-progress' | 'done' | 'cancelled'

// One assignee on an action (item 17, multi-assignee).
export interface ActionAssignee {
  id: number
  name: string | null
  avatarInitials: string | null
}

export interface EventAction {
  id: number
  eventId: number
  title: string
  description: string | null
  department: string | null
  // Primary assignee (back-compat) + the full multi-assignee set.
  assigneeId: number | null
  assigneeName: string | null
  assignees: ActionAssignee[]
  assigneeIds: number[]
  // Gantt link (item 11): the linked Project Plan task, and the pill state.
  planTaskId: number | null
  onPlan: boolean
  dueAt: string | null              // YYYY-MM-DD
  priority: ActionPriority
  status: ActionStatus
  completedAt: string | null         // ISO timestamp, set when status='done'
  completedByName: string | null
  createdAt: string
  createdByName: string | null
}

// Full body for create. priority/status default server-side if omitted.
export interface EventActionCreate {
  title: string
  description?: string | null
  department?: string | null
  assigneeId?: number | null        // legacy single assignee (still accepted)
  assigneeIds?: number[]            // multi-assignee set (preferred)
  addToPlan?: boolean               // create a linked Gantt task (default true)
  dueAt?: string | null
  priority?: ActionPriority
  status?: ActionStatus
}

// Patch body for partial updates (status toggle, inline edits). Every
// field optional — absent fields preserve current value server-side.
export interface EventActionPatch {
  title?: string
  description?: string | null
  department?: string | null
  assigneeId?: number | null        // legacy single assignee (still accepted)
  assigneeIds?: number[]            // replaces the full assignee set when present
  addToPlan?: boolean               // toggle the linked Gantt task on/off
  dueAt?: string | null
  priority?: ActionPriority
  status?: ActionStatus
}

// ---- Project plan / Gantt (spec 32) --------------------------------------
//
// Per-event Gantt tasks. dbo.EventPlanTasks (mig 028). MVP wire shape;
// dependsOnIds + isOverdue + isOnCriticalPath are spec-32 derived
// fields that land when the GanttStrip primitive + CRUD ship.

export type PlanGroup =
  | 'Bookings' | 'Marketing' | 'Programme' | 'Safety'
  | 'Staffing' | 'Operations' | 'Live' | 'Closeout'

export type PlanPriority = 'low' | 'medium' | 'high'

export const PLAN_GROUPS: ReadonlyArray<PlanGroup> = [
  'Bookings', 'Marketing', 'Programme', 'Safety',
  'Staffing', 'Operations', 'Live', 'Closeout'
] as const

export interface PlanTask {
  id: number
  eventId: number
  name: string
  description: string | null
  group: PlanGroup
  assigneeId: number | null
  assigneeName: string | null
  startAt: string             // YYYY-MM-DD
  endAt: string               // YYYY-MM-DD
  progress: number            // 0..100
  priority: PlanPriority
  notes: string | null
}

// POST body. group + name + startAt + endAt required; everything else
// optional. progress defaults to 0, priority to 'medium' server-side.
// dependsOnIds is a deferred follow-up (mig 028 has the table; no
// wire support yet).
export interface PlanTaskCreate {
  name: string
  group: PlanGroup
  startAt: string
  endAt: string
  description?: string | null
  assigneeId?: number | null
  progress?: number
  priority?: PlanPriority
  notes?: string | null
}

// PATCH body — partial. Absent fields preserve current value
// server-side. Same field set as create.
export interface PlanTaskPatch {
  name?: string
  group?: PlanGroup
  startAt?: string
  endAt?: string
  description?: string | null
  assigneeId?: number | null
  progress?: number
  priority?: PlanPriority
  notes?: string | null
}

// ---- Briefings & comms (spec 05) -------------------------------------------

export interface BriefingTopic {
  id: number
  title: string
  category: string
  durationMinutes: number
  bodyMarkdown: string
  requiredForRoles: string[] | null
  version: number
  isActive: boolean
  // Auto-generated event "overview" briefing (item 4) — system-managed.
  isOverview?: boolean
  // Guest FAQ library (mig 067). isFaq topics are guest Q&A (title = question,
  // bodyMarkdown = answer, with {{token}} placeholders); faqScope 'all' or an
  // event-type name limits where the FAQ applies.
  isFaq?: boolean
  faqScope?: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface BriefingTopicBody {
  title: string
  category: string
  durationMinutes: number
  bodyMarkdown: string
  requiredForRoles: string[] | null
  isFaq?: boolean
  faqScope?: string | null
}

// GET /api/events/:id/faq-suggestion — assembled guest FAQ markdown for the
// Product Briefing "Generate FAQ" button, with live tokens already filled.
export interface FaqSuggestion {
  markdown: string
  topicCount: number
}

export interface BriefingAck {
  userId: number
  userName: string | null
  userAvatar: string | null
  ackedAt: string
  isStale: boolean
}

export interface EventBriefingTopic extends BriefingTopic {
  addedAt: string
  sortOrder: number
  acks: BriefingAck[]
}

export interface EventBriefingsPayload {
  topics: EventBriefingTopic[]
  rostered: Array<{ userId: number; userName: string | null; userAvatar: string | null }>
}

export interface CommsSendResult {
  queued: number
  skipped: number
  total: number
  perUser?: Array<{ userId: number; result: string }>
}

// ---- Reports (spec 07) -----------------------------------------------------

export interface ReportsFilters {
  fromDate: string | null
  toDate: string | null
  types: string[]
  status: string[]
}

export interface ReportsPayload {
  kpis: {
    events: number
    attendees: number
    revenue: number
    volunteerHours: number
  }
  byMonth: Array<{ month: string; count: number }>
  attendance: Array<{ eventId: number; name: string; capacity: number; sold: number }>
  progress: Array<{ bucket: string; count: number }>
  variance: Array<{ eventId: number; name: string; variancePct: number | null }>
  // MERAC ticket revenue rolled up across the filtered events. Money is
  // BIGINT pence on the wire — render via formatGBP. Empty byEvent +
  // zero totals when no events have MeracEventMap bindings, or no
  // MeracSessions have landed yet, or the caller lacks hasFinancial
  // (server suppresses the data for non-financial users, same gate as
  // the variance section).
  meracRevenue: {
    totalGrossPence: number   // booked (advance)
    totalNetPence: number
    totalGatePence: number    // walk-up / gate takings
    totalPence: number        // booked + gate
    totalSessions: number
    byEvent: Array<{
      eventId: number
      name: string
      sessionCount: number
      grossPence: number
      netPence: number
      gatePence: number
      lastSessionAt: string | null
    }>
  }
}

// ---- Volunteer surface (spec 11) -------------------------------------------

export interface VolunteerShift {
  assignmentId: number
  date: string
  notes: string | null
  role: { id: number; name: string }
  event: {
    id: number
    name: string
    status: EventStatus
    startDate: string
    endDate: string
    location: string | null
    type: { name: string; colourBg: string; colourText: string }
  }
}

export interface VolunteerPendingBriefing {
  eventId: number
  eventName: string
  topicId: number
  title: string
  category: string
  topicVersion: number
  ackedAt: string | null
  isAcked: boolean
  isStale: boolean
}

export interface VolunteerShiftsPayload {
  shifts: VolunteerShift[]
  briefings: VolunteerPendingBriefing[]
}

export interface VolunteerProfile {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  emergencyContactName: string | null
  emergencyContactPhone: string | null
  isVolunteer: boolean
  onboardingCompletedAt: string | null
}

export interface VolunteerProfileBody {
  email: string | null
  phone: string | null
  emergencyContactName: string | null
  emergencyContactPhone: string | null
}

// ---- My Actions (spec 14) --------------------------------------------------

export interface MyActionsPayload {
  // Event Actions where I'm the assignee. Filtered to open + in-progress
  // server-side (terminal statuses excluded). Full editable shape so
  // /my-actions can pop the ActionEditor directly without a follow-up
  // fetch.
  actions: Array<{
    id: number
    title: string
    description: string | null
    department: string | null
    assigneeId: number | null
    dueDate: string | null
    priority: ActionPriority
    status: ActionStatus
    event: { id: number; name: string }
  }>
  // Project Plan tasks where I'm the assignee. Filtered to progress<100
  // server-side — completed tasks fall off the list. Full editable
  // shape so /my-actions can pop the PlanTaskEditor directly.
  tasks: Array<{
    id: number
    name: string
    description: string | null
    group: PlanGroup
    assigneeId: number | null
    startAt: string             // YYYY-MM-DD
    endAt: string               // YYYY-MM-DD
    progress: number            // 0..99 (100 filtered out server-side)
    priority: PlanPriority
    notes: string | null
    event: { id: number; name: string }
  }>
  checklistItems: Array<{
    id: number
    eventId: number
    eventName: string
    section: string
    title: string
    dueDate: string | null
  }>
  risks: Array<{
    id: number
    eventId: number
    eventName: string
    hazard: string
    residualScore: number
  }>
  briefings: Array<{
    eventId: number
    eventName: string
    topicId: number
    title: string
    topicVersion: number
    isStale: boolean
  }>
}

// ---- Admin users (spec 15) -------------------------------------------------

export interface AdminUser {
  id: number
  upn: string | null
  name: string | null
  avatarInitials: string | null
  roleLabel: string | null
  isAdmin: boolean
  isActive: boolean
  hasFinancial: boolean
  isVolunteer: boolean
  email: string | null
  phone: string | null
  department: { id: number; name: string } | null
  lastSeenAt: string | null
  magicLinkCount30d: number
}

// Admin user detail (spec 44). Same row shape as AdminUser plus the
// magic-link timeline (last 30 days, newest-first, capped at 100).
// revokedAt is always null for now — schema doesn't carry that column
// yet; surfaced as a snag.
export interface AdminUserDetail extends AdminUser {
  entraObjectId: string | null
  magicLinks: AdminMagicLinkRecord[]
}

// PATCH /api/access/users/{id} body. Both fields optional so a partial
// update can land just one. Empty/whitespace roleLabel normalises to
// null server-side.
export interface AdminUserPatch {
  roleLabel?: string | null
  hasFinancial?: boolean
}

// POST /api/access/users/from-entra response (Tweak T5). The api wrapper
// throws on non-2xx, so this union only covers the 2xx responses:
//   - 201: { user }           (newly created)
//   - 200: { error: 'already-exists', user } (row already in dbo.Users)
// 4xx/5xx surface as thrown Errors whose .message starts with the status
// (e.g. "404 Not Found: {\"error\":\"not-found-in-entra\"}").
export type CreateUserFromEntraResult =
  | { user: AdminUser }
  | { error: 'already-exists'; user: AdminUser | null }

// ---- Station maps (spec 31 / item 7) -------------------------------------
//
// Per-event markers on a station's base image. Coordinates normalised
// 0..1 (anchored to the base image's pixel dimensions, recorded in
// public/station-maps/manifest.json). Drift on the base image doesn't
// invalidate marker coords as long as the manifest's bbox is unchanged.
//
// Station + kind values mirror the CK constraints in mig 027 + 033.
// Drift here means the runtime validator (server-side) catches the
// 400 before the row hits SQL; this type just keeps the frontend
// honest.

export const STATION_SLUGS = [
  'pickering', 'levisham', 'goathland', 'grosmont', 'mpd', 'whitby'
] as const
export type StationSlug = typeof STATION_SLUGS[number]

// 18 kinds — facilities (permanent), staff/info points, deployable
// items, and zone/wayfinding pins. Grouped for the marker picker UX;
// the underlying CK accepts any in the list.
export const STATION_MARKER_KINDS = [
  // Permanent facilities
  'facility-toilets', 'facility-first-aid', 'facility-defib',
  // People & info points
  'staff-post', 'photo-point', 'hazard', 'comms-point',
  // Deployable items (mig 033)
  'item-portable-toilet', 'item-generator', 'item-signage',
  'item-food-stall', 'item-marquee', 'item-fencing', 'item-bin',
  // Zones & wayfinding (mig 033)
  'parking-zone', 'entrance', 'exit-emergency', 'meeting-point'
] as const
export type StationMarkerKind = typeof STATION_MARKER_KINDS[number]

// linkedRoleSlotId column dropped in migration 039 (legacy RoleSlots
// table gone). linkedActivityId stays — that FK to dbo.Activities
// survived the roster rebuild. A future rewire to the new Roles +
// RosterAssignments model can re-introduce a roster-link if needed.
export interface StationMarker {
  id: number
  eventId: number
  station: StationSlug
  kind: StationMarkerKind
  label: string
  description: string | null
  x: number                       // 0..1
  y: number                       // 0..1
  linkedActivityId: number | null
  createdAt: string
  createdByName: string | null
}

export interface StationMarkerCreate {
  station: StationSlug
  kind: StationMarkerKind
  label: string
  description?: string | null
  x: number
  y: number
  linkedActivityId?: number | null
}

export interface StationMarkerPatch {
  station?: StationSlug
  kind?: StationMarkerKind
  label?: string
  description?: string | null
  x?: number
  y?: number
  linkedActivityId?: number | null
}

// /station-maps/manifest.json — generated by scripts/fetch-station-base-map.mjs.
// Keys are StationSlug values; missing entries mean "no base image yet"
// and the Map tab renders an empty state for that station.
export interface StationMapManifest {
  attribution: string
  sourceUrl: string
  generatedAt: string
  stations: Partial<Record<StationSlug, {
    imagePath: string                                  // /station-maps/<slug>.jpg
    width: number
    height: number
    bbox: { south: number; west: number; north: number; east: number }
    description: string
  }>>
}

export interface AdminMagicLinkRecord {
  id: number
  purpose: 'sign-in' | 'briefing-ack' | 'duty-detail' | 'callback' | null
  issuedAt: string
  expiresAt: string
  consumedAt: string | null
  revokedAt: string | null
  consumedFrom?: { ip?: string; userAgent?: string }
}

// NYMR station locations for the event Location dropdown (item 6). The
// passenger stations down the line, plus the Grosmont depot and a couple of
// practical line-wide options. Stored verbatim in Events.location (NVARCHAR) —
// no migration — and the picker keeps an "Other…" escape hatch so one-off or
// pre-existing free-text locations are preserved.
export const STATION_LOCATIONS = [
  'Pickering',
  'Levisham',
  'Newton Dale Halt',
  'Goathland',
  'Grosmont',
  'Grosmont MPD',
  'Whitby',
  'Whole line'
] as const
export type StationLocation = typeof STATION_LOCATIONS[number]

// ---- Per-event resources (Live View PR L1 / spec 23, mig 048) -------------
//
// Categories are slugs (kebab-case) — public contract. They match the
// six tab keys in the v5 prototype's ResourcesPanel (line 3651-3657)
// and the CK constraint on dbo.EventResources. Add new ones; never
// rename a shipped slug.
export const EVENT_RESOURCE_CATEGORIES = [
  'briefing',
  'plan',
  'activities',
  'maps',
  'contacts',
  'first-aid'
] as const
export type EventResourceCategory = typeof EVENT_RESOURCE_CATEGORIES[number]

// UI-facing label per category. Mirrors the FLAG_CATEGORY_LABEL pattern.
// Public contract from day 1 — never rename, only add.
export const EVENT_RESOURCE_CATEGORY_LABEL: Record<EventResourceCategory, string> = {
  'briefing':   'Briefing',
  'plan':       'Plan',
  'activities': 'Activities',
  'maps':       'Maps',
  'contacts':   'Contacts',
  'first-aid':  'First Aid'
}

export interface EventResource {
  id: number
  eventId: number
  category: EventResourceCategory
  title: string
  description: string | null
  href: string
  icon: string | null
  sortOrder: number
  isAutoSeeded: boolean
  createdAt: string
  createdById: number | null
  createdByName: string | null
}

export interface EventResourceAttach {
  category: EventResourceCategory
  title: string
  description?: string | null
  href: string
  icon?: string | null
  sortOrder?: number
}

// ---- Quick Reference (Resources Panel rebuild, spec 23 v2, mig 052) -------
//
// Backs the six-tab self-contained quick-reference document surfaced by
// ResourcesPanel.tsx. Distinct from EventResource (the link-shortcut
// rows used by the spec 46 Resources Directory grid).
//
// `operatingPattern` / `todaysProgramme` / `visitorFlow` are nullable
// per-event narrative (mig 052 columns). NULL → render NYMR-generic
// fallback from src/components/live/resourcesData.ts.

export interface QuickRefActivity {
  id: number
  name: string
  icon: string | null
  description: string | null
  location: string | null
  timeText: string | null
  category: string | null
}

export interface QuickRefContact {
  userId: number
  name: string
  roleLabel: string | null         // dbo.Users.role_label (Entra org-chart title)
  rosterRole: string               // dbo.Roles.name
  rosterRoleSort: number           // dbo.Roles.sort_order
  phone: string | null
  avatarInitials: string | null
}

export interface QuickReferencePayload {
  eventId: number
  eventName: string
  location: string | null
  operatingPattern: string | null
  todaysProgramme: string | null
  visitorFlow: string | null
  activities: QuickRefActivity[]
  contacts: QuickRefContact[]
}

// ---- Live View (Live View PR L2 / spec 33 §7, migs 049+050) ---------------
//
// Six alarm categories (prototype L2745-2757). Slug values; UI does
// slug → label render via FLAG_CATEGORY_LABEL. Public contract from day
// 1 (engineering lessons §10) — never rename these strings.
export const FLAG_CATEGORIES = [
  'medical-first-aid',
  'evacuation',
  'lost-child',
  'crowd-control',
  'train-issue',
  'other'
] as const
export type FlagCategory = typeof FLAG_CATEGORIES[number]

export const FLAG_CATEGORY_LABEL: Record<FlagCategory, string> = {
  'medical-first-aid': 'Medical / First Aid',
  'evacuation':        'Evacuation',
  'lost-child':        'Lost Child',
  'crowd-control':     'Crowd Control',
  'train-issue':       'Train Issue',
  'other':             'Other'
}

export interface LiveTimelineItem {
  id: number
  label: string
  time: string                    // HH:MM 24-hour
  loco: string | null
  origin: string | null
  destination: string | null
  capacity: number | null
  booked: number
  status: 'done' | 'now' | 'next' | ''
}

export interface LiveFeedEntry {
  id: number
  occurredAt: string
  summary: string
  action: string
  entityType: string
  actorName: string | null
  actorAvatar: string | null
}

export interface LiveShift {
  id: number
  userId: number
  userName: string | null
  userAvatar: string | null
  signedOnDay: number
  signedOnAt: string
  signedOffAt: string | null
}

export interface LiveFlag {
  id: number
  category: FlagCategory
  body: string
  raisedById: number
  raisedByName: string | null
  raisedByAvatar: string | null
  createdAt: string
}

export interface LiveMyDuty {
  assignmentId: number
  roleId: number
  roleName: string
  assignmentDate: string
  timeStart: string | null
  timeEnd: string | null
  note: string | null
}

export interface LiveWalkthrough {
  completedAt: string | null
  completedById: number | null
  completedByName: string | null
}

export interface LivePulsePayload {
  now: string
  lastUpdatedAt: string
  targetDate: string
  dayOfEvent: number | null
  totalDays: number
  walkthrough: LiveWalkthrough
  timeline: LiveTimelineItem[]
  feed: LiveFeedEntry[]
  shifts: LiveShift[]
  flags: LiveFlag[]
  myDuties: LiveMyDuty[]
}

// Wire response from sign-on / sign-off. Includes eventId so the client
// can scope state when sign-ons across multiple events are surfaced
// concurrently (PR L4's PersonDutyModal).
export interface LiveShiftRecord {
  id: number
  eventId: number
  userId: number
  signedOnDay: number
  signedOnAt: string
  signedOffAt: string | null
}

// ---- Live View flags / walkthrough (PR L3) --------------------------------
//
// Wire shape from /api/events/{id}/live-flags. category narrows to the six
// public-contract slugs (mig 049's CK constraint).
export interface LiveFlagRecord {
  id: number
  eventId: number
  category: FlagCategory
  body: string
  raisedById: number
  raisedByName: string | null
  raisedByAvatar: string | null
  resolvedAt: string | null
  resolvedById: number | null
  resolvedByName: string | null
  createdAt: string
}

// POST body for /api/events/{id}/live-flags. body is the optional free-text
// note alongside the category — AlarmModal sends category-only by default.
export interface LiveFlagCreateBody {
  category: FlagCategory
  body?: string | null
}

// PATCH /api/events/{id}/walkthrough response shape. completedAt + ById are
// the canonical fields written by the dedicated walkthrough toggle handler.
export interface EventWalkthroughState {
  eventId: number
  walkthroughCompletedAt: string | null
  walkthroughCompletedById: number | null
  walkthroughCompletedByName: string | null
}

// ---- Event broadcasts (Live View PR L4, mig 051) --------------------------
//
// Manager-only one-way message log surfaced by the BroadcastModal.
// recipient_count is captured server-side at send time from the
// currently-open shifts — snapshot rather than derived.
export interface BroadcastRecord {
  id: number
  eventId: number
  sentById: number
  sentByName: string | null
  sentByAvatar: string | null
  body: string
  sentAt: string
  recipientCount: number
}

export interface BroadcastCreateBody {
  body: string
}

// ---- Resources Directory (spec 46) ---------------------------------------
//
// Cross-event matrix surfaced at /resources. One row per non-archived
// event; counts map keyed by the 6 public-contract category slugs.
export interface ResourcesDirectoryEvent {
  eventId: number
  eventName: string
  startDate: string                 // YYYY-MM-DD
  endDate: string
  counts: Record<EventResourceCategory, number>
}

export interface ResourcesDirectoryPayload {
  categories: ReadonlyArray<EventResourceCategory>
  events: ResourcesDirectoryEvent[]
}

// ---- Ticketing (Feature 4, migs 064/065) ---------------------------------
//
// TicketType   = the standard admin catalogue (dropdown source).
// EventTicketType = the per-event selection: which standard types apply, at
//   what price. meracMappings/sales are the phased MERAC layer — mappings key
//   on PriceLookups.pluRecNo (= MeracSessionBookings.ticket_class_merac_id),
//   sales are only populated for financial users on mapped types.
export const TICKET_TYPE_CATEGORIES = ['Adult', 'Child', 'Member', 'Concession', 'Group', 'Access', 'Other'] as const
export type TicketTypeCategory = typeof TICKET_TYPE_CATEGORIES[number]

export interface TicketType {
  id: number
  name: string
  category: TicketTypeCategory
  isConcession: boolean
  defaultPricePence: number | null   // standard price; pre-fills per-event (mig 068)
  isActive: boolean
  sortOrder: number
}

export interface TicketTypeCreate {
  name: string
  category: TicketTypeCategory
  isConcession: boolean
  defaultPricePence: number | null
  isActive: boolean
  sortOrder: number
}

export type TicketTypePatch = TicketTypeCreate

export interface TicketTypeMeracMapping {
  ticketClassMeracId: string
  meracName: string | null
}

export interface TicketTypeSales {
  quantityNet: number
  grossPence: number
  netPence: number
  netPending: boolean
}

export interface EventTicketType {
  id: number
  eventId: number
  ticketTypeId: number
  name: string
  category: string
  isConcession: boolean
  pricePence: number | null
  onSale: boolean
  allocation: number | null
  notes: string | null
  sortOrder: number
  meracMappings: TicketTypeMeracMapping[]
  sales: TicketTypeSales | null
}

export interface EventTicketTypeCreate {
  ticketTypeId: number
  pricePence: number | null
  onSale: boolean
  allocation: number | null
  notes: string | null
  sortOrder: number
}

export interface EventTicketTypePatch {
  pricePence: number | null
  onSale: boolean
  allocation: number | null
  notes: string | null
  sortOrder: number
}

export interface TicketMeracCandidate {
  ticketClassMeracId: string
  meracName: string | null
  quantityNet: number
  grossPence: number
}

// Resolved ticket pricing for the Product Briefing PDF §6 and FAQ generation
// (GET /api/events/:id/briefing-tickets). Configured on-sale ticket types with
// their prices, falling back to live MERAC unit prices, or the MERAC classes
// directly. Unit price only — safe for non-financial users.
export interface BriefingTicket {
  name: string
  pricePence: number | null
  notes: string | null
  isConcession: boolean
  source: 'configured' | 'merac'
}

// ---- Event flags (Claude Help Phase 3, mig 069) --------------------------
export interface EventFlag {
  id: number
  eventId: number
  eventName: string
  eventStartDate: string
  kind: string
  severity: 'info' | 'warning' | 'urgent'
  title: string
  detail: string | null
  createdAt: string
}

// ---- Catering (Feature 3, mig 063) ---------------------------------------
export const CATERING_STATUSES = ['confirmed', 'tbc', 'proposed'] as const
export type CateringStatus = typeof CATERING_STATUSES[number]

export interface EventCatering {
  id: number
  eventId: number
  offer: string
  supplier: string | null
  costPence: number | null           // supplier cost — finance-gated (null when hidden)
  retailPricePence: number | null    // customer/retail price — finance-gated (mig 066)
  costNote: string | null
  status: CateringStatus
  isUpsell: boolean
  notes: string | null
  sortOrder: number
}

export interface EventCateringBody {
  offer: string
  supplier: string | null
  costPence: number | null
  retailPricePence: number | null
  costNote: string | null
  status: CateringStatus
  isUpsell: boolean
  notes: string | null
  sortOrder: number
}

export interface CateringReportEvent {
  eventId: number
  eventName: string
  startDate: string
  endDate: string
  items: EventCatering[]
}

export interface CateringReportPayload {
  events: CateringReportEvent[]
}

// ---- Product Briefing (Feature 1, mig 061) -------------------------------
// Editable prose sections (markdown). Schedule/capacity/pricing in the printed
// briefing are pulled live from Services / EventTicketTypes at render.
export interface EventBriefingDoc {
  heroTagline: string | null
  subtitle: string | null
  overviewMd: string | null
  conceptMd: string | null
  guestJourneyMd: string | null
  marketingMd: string | null
  ticketingNotesMd: string | null
  faqMd: string | null
}

// ---- Forecast Studio (migs 070-073) --------------------------------------
// Model a candidate event ('Idea') for viability before committing it to the
// operating plan. All money is integer pence on the wire; derived figures
// (net incremental, P&L totals, benchmark averages) are NEVER persisted — they
// are recomputed at render from these stored inputs.

// One accepted market-research finding (rendered as a border-left card).
export interface Insight {
  icon: string
  tag: string
  color: string   // borderLeft accent (hex)
  head: string
  body: string
}

// A per-day timetable-context cell for the proposed slot (resolved at slot-save
// from The Big Plan season timetable; mocked until the cross-app read lands).
export interface ForecastProposedDay {
  date: string        // ISO YYYY-MM-DD
  tt: string          // timetable code: blue|red|pink|event|none
  proposed: boolean   // true for the event's own dates, false for context days
}

export interface ForecastTeamRow {
  id: number
  personId: number
  personName: string
  functionId: number
  functionTitle: string
  kind?: 'standard' | 'exec-signoff'
  dept: string
  commitmentText: string | null
  approvalStatus: 'pending' | 'approved' | 'blocked'
  // Function-block response loop (written by the emailed confirm link, EM-2).
  responseStatus?: 'confirmed' | 'commented' | 'red-alert' | null
  responseComment?: string | null
  respondedAt?: string | null
}

export interface ForecastPhase {
  id: number
  label: string
  dept: string
  startWeeksBefore: number
  endWeeksBefore: number
  ownerPersonId: number | null
}

// Change Notice (mig 077) — a formal change notice raised against a forecast,
// synced to The Big Plan as a CMN on commit. author/update/reply is the role the
// contributor picks; comment/update/alert is the nature of a feed message.
export type CnStatus = 'draft' | 'submitted' | 'approved' | 'rejected'
export type CnSignStatus = 'pending' | 'approved' | 'rejected'
export type CnFeedKind = 'comment' | 'update' | 'alert'
export type CnAuthorRole = 'author' | 'update' | 'reply'

export interface CnSignoffRow {
  id: number
  role: string
  name: string | null
  personId: number | null
  status: CnSignStatus
  signedAt: string | null
  signedByName: string | null
  sortOrder: number
}

export interface CnFeedRow {
  id: number
  kind: CnFeedKind
  authorRole: CnAuthorRole
  body: string
  authorId: number | null
  authorName: string | null
  createdAt: string | null
}

export interface ChangeNotice {
  id: number
  eventId: number
  title: string
  whatIs: string | null
  whatChanges: string | null
  affectedDepts: string[]
  reqs: { documentation: boolean; process: boolean; training: boolean; comms: boolean }
  goLive: string | null
  blockers: string[]
  thoughts: string | null
  status: CnStatus
  bpCmnId: string | null
  bpRef: string | null
  syncedAt: string | null
  createdById: number | null
  createdByName: string | null
  createdAt: string | null
  updatedAt: string | null
  signoff: CnSignoffRow[]
  feed: CnFeedRow[]
}

// Any subset of these is applied by PUT /forecast/change-notice.
export interface ChangeNoticeBody {
  title?: string
  whatIs?: string | null
  whatChanges?: string | null
  affectedDepts?: string[]
  reqs?: { documentation: boolean; process: boolean; training: boolean; comms: boolean }
  goLive?: string | null
  blockers?: string[]
  thoughts?: string | null
  status?: 'draft' | 'submitted'
}

// A linked comparable/past event whose REAL financials form the benchmark.
// Money fields are null for non-financial users.
export interface ForecastBaselineRow {
  sourceEventId: number
  name: string
  when: string                    // human label, e.g. "Aug 2025"
  revenuePence: number | null
  attend: number | null
  yieldPence: number | null
  selected: boolean
}

// Flexible forecast money lines (Sprint 1.5). Ticket revenue is NOT a line
// (att×yield). `scales` = does this revenue line grow with attendance.
export interface ForecastRevenueLine { key: string; label: string; pence: number; source: string; scales: boolean }
export interface ForecastCostLine { key: string; label: string; pence: number; source: string }
export interface ForecastManualBaseline { attendance: number | null; revenuePence: number | null; yieldPence: number | null; costsPence: number | null; note: string }

// A contract (supplier/contractor) underpinning the event. Fees feed the P&L;
// the rest is business-case metadata. Money is integer pence.
export interface ContractConcern { area: string; severity: 'info' | 'watch' | 'high'; concern: string; evidence?: string; recommendation?: string }
export interface ForecastContract {
  id: number
  eventId: number
  companyName: string
  companyType: 'supplier' | 'contractor'
  startDate: string | null
  endDate: string | null
  timelineNote: string | null
  feePence: number
  advancePaymentPence: number
  advanceDue: string | null
  penaltyNote: string | null
  penaltyPence: number | null
  redFlagNote: string | null
  redFlagSeverity: 'none' | 'watch' | 'high'
  hasDoc: boolean
  docFilename: string | null
  docUploadedAt: string | null
  aiSummary: string | null
  aiConcerns: ContractConcern[]
  aiAnalysedAt: string | null
}
export interface ForecastContractBody {
  companyName: string
  companyType: 'supplier' | 'contractor'
  startDate: string | null
  endDate: string | null
  timelineNote: string | null
  feePence: number
  advancePaymentPence: number
  advanceDue: string | null
  penaltyNote: string | null
  penaltyPence: number | null
  redFlagNote: string | null
  redFlagSeverity: 'none' | 'watch' | 'high'
  sortOrder: number
}

// The full forecast payload for one Idea event (GET/PUT /events/{id}/forecast).
export interface ForecastPayload {
  eventId: number
  status: 'modelling' | 'in-approval' | 'committed'
  attendanceFc: number | null
  ticketYieldPence: number | null
  scenario: { att: number; yieldPence: number; costAdj: number }
  proposed: {
    start: string | null
    end: string | null
    dates: string[]
    days: ForecastProposedDay[]
    displacedTt: string | null
    normalDayPence: number | null
  }
  brief: { sector: string; comps: string; questions: string; concept: string }
  insights: Insight[]
  researchSummary: string
  revenueStreams: ForecastRevenueLine[]
  costLines: ForecastCostLine[]
  manualBaseline: ForecastManualBaseline | null
  baseline: ForecastBaselineRow[]
  team: ForecastTeamRow[]
  phases: ForecastPhase[]
  contracts: ForecastContract[]
  contractFeesPence: number
  // Convenience: the season label used in the proposed-slot card header.
  season: string | null
}

export type ForecastCommitResult = {
  ok: boolean
  published: boolean
  publishError?: string | null
  eventId: number
  actionUrl?: string | null
}

// Core NYMR Functions library (admin-managed reusable delivery functions).
export interface LibraryFunction {
  id: number
  slug: string
  title: string
  dept: string
  kind: 'standard' | 'exec-signoff'
  defaultResponsibilities: string[]
  defaultLeadWeeks: number
  isActive: boolean
  sortOrder: number
  usedInCount?: number
}

export interface LibraryFunctionBody {
  slug: string
  title: string
  dept: string
  kind?: 'standard' | 'exec-signoff'
  defaultResponsibilities: string[]
  defaultLeadWeeks: number
}

// Finance "Forecast" view — read-only mirror of the committed scenario vs the
// event's Budget plan. Financial-gated. Money in pence.
export interface ForecastFinancePayload {
  hasForecast: boolean
  grossRevenuePence: number
  totalCostsPence: number
  netPence: number
  netIncrementalPence: number
  streams: Array<{ label: string; forecastPence: number; planPence: number }>
  plan: { revenuePence: number; costsPence: number; netPence: number }
}

// Market research (POST /api/forecast/research). mode 'chat' → { reply };
// mode 'run' → { insights }.
export interface ResearchChatResponse { reply: string; degraded?: boolean }
export interface ResearchRunResponse { insights: Insight[]; summary?: string; degraded?: boolean }

// A versioned business-plan document (Claude-generated or human-uploaded).
export interface ForecastPlanDoc {
  id: number
  version: number
  source: 'claude' | 'uploaded'
  filename: string
  contentType: string
  createdAt: string
  createdByName: string | null
}

// ---- Programming Overview — Calendar tab 1 (spec 41, mig 079) --------------
// The editable annual planner. A ProgrammeBlock is a single-day placement of a
// named block; a multi-day block is several blocks sharing a title. Distinct
// from the operational Events grid (Calendar tab 2).
export interface ProgrammeCategory {
  id: number
  label: string
  colour: string          // '#RRGGBB'
  sortOrder: number
  isActive: boolean
}
export interface ProgrammeBlock {
  id: number
  blockDate: string       // 'YYYY-MM-DD'
  title: string
  categoryId: number
  sortOrder: number
  clientId: string | null
}
export interface ProgrammeData {
  year: number
  title: string | null
  subtitle: string | null
  categories: ProgrammeCategory[]
  blocks: ProgrammeBlock[]
}
export interface ProgrammeBlockCreate {
  title: string
  categoryId: number
  dates?: string[]
  startDate?: string
  endDate?: string
}
export interface ProgrammeBlockPatch {
  title?: string
  categoryId?: number
  blockDate?: string
  sortOrder?: number
}
export interface ProgrammeCategoryCreate {
  label: string
  colour: string
  sortOrder?: number
}
export interface ProgrammeCategoryPatch {
  label?: string
  colour?: string
  sortOrder?: number
  isActive?: boolean
}
// The portable planner JSON shape (import/export round-trips this).
export interface ProgrammeExport {
  events: Record<string, { id: string; date: string; title: string; categoryId: string; order: number }>
  categories: Array<{ id: string; label: string; color: string }>
  subtitle: string
}
